import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Bitcoin, Coins, AlertTriangle, ShieldCheck, Pencil, Check, X, Circle, CheckCircle2, ExternalLink } from "lucide-react";

/**
 * Admin · Payment methods
 *
 * Toggle any method on/off, rename/rewrite its label + description in place
 * (backend PATCH accepts label + sub), and view per-integration setup
 * checklists when a method is blocked by missing env keys.
 */
export default function AdminPayments() {
  const [methods, setMethods] = useState(null);
  const [busy, setBusy] = useState({});
  const [editing, setEditing] = useState(null); // { code, label, sub }

  const load = () => {
    api.get("/admin/payment-methods")
      .then((r) => setMethods(r.data.methods || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load payment methods."));
  };
  useEffect(load, []);

  const toggle = async (code, next) => {
    setBusy((b) => ({ ...b, [code]: true }));
    setMethods((cur) => (cur || []).map((m) => m.code === code ? { ...m, admin_enabled: next, enabled: next && !m.blocked_by_integration } : m));
    try {
      await api.patch(`/admin/payment-methods/${code}`, { enabled: next });
      toast.success(`${code} ${next ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update method.");
      setMethods((cur) => (cur || []).map((m) => m.code === code ? { ...m, admin_enabled: !next } : m));
    } finally {
      setBusy((b) => ({ ...b, [code]: false }));
    }
  };

  const startEdit = (m) => setEditing({ code: m.code, label: m.label, sub: m.sub || "" });
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.label.trim()) {
      toast.error("Label cannot be empty."); return;
    }
    const code = editing.code;
    setBusy((b) => ({ ...b, [code]: true }));
    try {
      await api.patch(`/admin/payment-methods/${code}`, { label: editing.label.trim(), sub: editing.sub.trim() });
      toast.success(`${code} updated`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save changes.");
    } finally {
      setBusy((b) => ({ ...b, [code]: false }));
    }
  };

  if (!methods) return <div className="p-8 text-sm font-mono uppercase tracking-wider text-[#9AA3AF]">Loading payment methods…</div>;

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="admin-payments-page">
      <div>
        <div className="label-eyebrow text-[#3FFFE0]"><ShieldCheck className="inline h-3.5 w-3.5 mr-1" /> Checkout</div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold mt-1">Payment methods.</h1>
        <p className="text-sm text-[#9AA3AF] mt-2 max-w-2xl">
          Toggle any option on or off, or click the pencil to rebrand the label and description
          shown at checkout — no code deploy required.
        </p>
      </div>

      <div className="border border-[#1F2937] rounded-md overflow-hidden bg-[#0E141B]">
        <table className="w-full text-sm">
          <thead className="bg-[#0B1017] text-[10.5px] uppercase tracking-[0.16em] text-[#9AA3AF] font-mono">
            <tr>
              <th className="text-left px-4 py-3">Method</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Description</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F2937]">
            {methods.map((m) => {
              const isEditing = editing?.code === m.code;
              const showSetup = m.setup && m.blocked_by_integration;
              return (
                <>
                  <tr key={m.code} data-testid={`pm-row-${m.code}`}>
                    <td className="px-4 py-3.5 align-top">
                      <div className="flex items-start gap-2.5">
                        <MethodIcon code={m.code} />
                        <div className="flex-1 min-w-0">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editing.label}
                              onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                              className="w-full px-2 py-1 rounded border border-[#3FFFE0] bg-[#0B1017] text-sm text-white outline-none"
                              data-testid={`pm-edit-label-${m.code}`}
                              maxLength={120}
                              autoFocus
                            />
                          ) : (
                            <div className="text-white font-medium">{m.label}</div>
                          )}
                          <div className="font-mono text-[10.5px] text-[#9AA3AF] uppercase tracking-[0.14em] mt-0.5">{m.code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell align-top">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editing.sub}
                          onChange={(e) => setEditing({ ...editing, sub: e.target.value })}
                          placeholder="Optional subtitle at checkout"
                          className="w-full px-2 py-1 rounded border border-[#3FFFE0] bg-[#0B1017] text-[12.5px] text-white outline-none"
                          data-testid={`pm-edit-sub-${m.code}`}
                          maxLength={200}
                        />
                      ) : (
                        <span className="text-[#9AA3AF] text-[12.5px]">{m.sub || <span className="italic text-[#4F5560]">—</span>}</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 align-top">
                      {m.enabled ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider bg-[#0B3B2F] text-[#3FFFE0]">
                          Live
                        </span>
                      ) : m.admin_enabled && m.blocked_by_integration ? (
                        <div className="inline-flex flex-col gap-1">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider bg-[#3B2F0B] text-[#FFB454]">
                            <AlertTriangle className="h-3 w-3" /> Setup required
                          </span>
                          <span className="text-[11px] text-[#9AA3AF]">See checklist below ↓</span>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-wider bg-[#3B0714] text-[#FF6B8A]">
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-right align-top">
                      {isEditing ? (
                        <div className="inline-flex items-center gap-1.5">
                          <button
                            onClick={saveEdit}
                            disabled={busy[m.code]}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded border border-[#3FFFE0]/60 bg-[#3FFFE0]/10 text-[#3FFFE0] text-[12px] hover:bg-[#3FFFE0]/20 disabled:opacity-50"
                            data-testid={`pm-save-${m.code}`}
                          >
                            <Check className="h-3.5 w-3.5" /> Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#1F2937] text-[#9AA3AF] text-[12px] hover:text-white"
                            data-testid={`pm-cancel-${m.code}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => startEdit(m)}
                            className="inline-flex items-center gap-1 text-[12px] text-[#9AA3AF] hover:text-[#3FFFE0]"
                            title="Edit label + description"
                            data-testid={`pm-edit-${m.code}`}
                          >
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </button>
                          <Toggle
                            checked={m.admin_enabled}
                            onChange={(next) => toggle(m.code, next)}
                            disabled={!!busy[m.code]}
                            testid={`pm-toggle-${m.code}`}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                  {showSetup && (
                    <tr key={`${m.code}-setup`} data-testid={`pm-setup-${m.code}`}>
                      <td colSpan={4} className="px-4 py-4 bg-[#0B1017]">
                        <div className="border border-[#FFB454]/30 bg-[#FFB454]/5 rounded-md p-4">
                          <div className="flex items-start gap-2.5">
                            <AlertTriangle className="h-4 w-4 text-[#FFB454] shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#FFB454]">
                                Configure {m.setup.provider}
                              </div>
                              <p className="text-[12.5px] text-[#9AA3AF] mt-1 leading-relaxed">
                                This method is enabled but won&apos;t appear at checkout until the following environment
                                variables are set on the backend. Add them to your <span className="font-mono text-white">/app/backend/.env</span> file and restart the backend service — the checklist auto-refreshes on this page.
                              </p>
                              <ul className="mt-3 space-y-2">
                                {m.setup.requirements.map((r) => (
                                  <li key={r.env_key} className="flex items-start gap-2.5 text-[12.5px]" data-testid={`pm-req-${r.env_key}`}>
                                    {r.configured ? (
                                      <CheckCircle2 className="h-4 w-4 text-[#3FFFE0] shrink-0 mt-0.5" />
                                    ) : (
                                      <Circle className="h-4 w-4 text-[#FFB454] shrink-0 mt-0.5" />
                                    )}
                                    <div className="flex-1">
                                      <span className="font-mono text-white">{r.env_key}</span>
                                      <span className={`ml-2 font-mono text-[10px] uppercase tracking-wider ${r.configured ? "text-[#3FFFE0]" : "text-[#FFB454]"}`}>
                                        {r.configured ? "configured" : "missing"}
                                      </span>
                                      <div className="text-[#9AA3AF] mt-0.5">{r.description}</div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                              {m.setup.docs_url && (
                                <a
                                  href={m.setup.docs_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 mt-3 text-[12px] text-[#3FFFE0] hover:text-white"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" /> {m.setup.provider} setup docs
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[11.5px] text-[#9AA3AF] leading-relaxed">
        Disabled methods are hidden from the checkout UI and rejected server-side — even a buyer
        who kept an old tab open cannot place an order using a method that&apos;s currently off.
      </p>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      className={`relative inline-flex items-center h-7 w-12 rounded-full transition ${
        checked ? "bg-[#0B6623]" : "bg-[#1F2937]"
      } ${disabled ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function MethodIcon({ code }) {
  if (code === "card") return <CreditCard className="h-5 w-5 text-[#3FFFE0]" strokeWidth={1.6} />;
  if (code === "crypto_btc") return <Bitcoin className="h-5 w-5 text-[#FFB454]" strokeWidth={1.6} />;
  return <Coins className="h-5 w-5 text-[#3FFFE0]" strokeWidth={1.6} />;
}
